import {
  action, computed, makeAutoObservable, observable, observe, runInAction,
} from 'mobx';
import { AppPrograms, AppTypes } from '@gamiphy/bl/app';
import { AppApi } from '@gamiphy/gamix-modules/app';
import { Http } from '@gamiphy/http';
import { Languages } from '@coretava/i18n';
import Cookies from 'js-cookie';
import { addToDate } from '@gamiphy/utils/date';
import { AppDto, AppDtoLanguageEnum } from '@coretava/api/core-service';

class AppStore {
  /**
   * Cookie to save the app id
   * @private
   */
  private static readonly APP_COOKIE_NAME = '_gamiphy_app';

  /**
   * App id cookie timeout
   * We cannot leave expires blank, else the cookie will be removed after the browser being closed
   * So we set the cookie 2 years long
   * @private
   */
  private static readonly APP_TIMEOUT_YEARS = 2;

  @observable initialized = false;

  @observable appId?: string = undefined;

  @observable app!: AppDto;

  constructor() {
    makeAutoObservable(this);
    this.watchAppIdToSaveInCookie();
  }

  @computed get dir(): 'rtl' | 'ltr' {
    const currentLang = document.documentElement.lang ?? this.app.language;
    switch (currentLang as Languages) {
      case Languages.ar:
      case Languages.he:
        return 'rtl';
      default:
        return 'ltr';
    }
  }

  @computed get prefLang(): AppDtoLanguageEnum {
    return this.app.language;
  }

  @computed get isCashbackProgram(): boolean {
    return this.app.program === AppPrograms.cashback;
  }

  @computed get isOmniApp(): boolean {
    return [AppTypes.coreLoyalty, AppTypes.fisico].every((type) => this.app?.types.includes(type));
  }

  @computed get isOfflineApp(): boolean {
    return !!this.app?.types.includes(AppTypes.fisico);
  }

  @computed get isOfflineOnlyApp(): boolean {
    return !!this.app?.types.includes(AppTypes.fisico) && !this.isOnlineApp;
  }

  @computed get isOnlineApp(): boolean {
    return !!this.app?.types.includes(AppTypes.coreLoyalty);
  }

  @computed get isOnlineOnlyApp(): boolean {
    return !!this.app?.types.includes(AppTypes.coreLoyalty) && !this.isOfflineApp;
  }

  @action
  public async init(appId: string) {
    if (!this.initialized) {
      Http.setGamixAppId(appId);
      this.appId = appId;

      const app = await AppApi.getApp(appId);

      if (app.enabled) {
        runInAction(() => {
          this.app = app as AppDto;
          this.initialized = true;
        });
      }
    }
  }

  /**
   * Get app id from cookies
   * @private
   */
  public getAppIdFromCookie(): string | undefined {
    return Cookies.get(AppStore.APP_COOKIE_NAME);
  }

  /**
   * Save the app id to the cookies
   * @private
   */
  private watchAppIdToSaveInCookie(): void {
    observe(this, 'appId', () => {
      if (this.appId) {
        Cookies.set(AppStore.APP_COOKIE_NAME, this.appId, {
          expires: addToDate(new Date(), 'years', AppStore.APP_TIMEOUT_YEARS),
        });
      }
    });
  }
}

export default new AppStore();
